import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiBriefcase, FiLayout, FiPalette, FiShoppingBag, FiTool } = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiHome,
      title: 'Residential Design',
      description: 'Complete home makeovers including living rooms, bedrooms, kitchens, and bathrooms.',
      features: ['Space Planning', 'Color Consultation', 'Furniture Selection', 'Lighting Design'],
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      icon: FiBriefcase,
      title: 'Commercial Design',
      description: 'Professional office spaces, retail stores, restaurants, and corporate environments.',
      features: ['Brand Integration', 'Employee Productivity', 'Customer Experience', 'Functional Layout'],
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      icon: FiLayout,
      title: 'Space Planning',
      description: 'Optimize your space layout for maximum functionality and aesthetic appeal.',
      features: ['Floor Plan Design', 'Traffic Flow', 'Storage Solutions', 'Multi-functional Spaces'],
      image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      icon: FiPalette,
      title: 'Color Consultation',
      description: 'Expert color selection to create the perfect mood and atmosphere.',
      features: ['Color Psychology', 'Paint Selection', 'Accent Colors', 'Lighting Coordination'],
      image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      icon: FiShoppingBag,
      title: 'Furniture & Decor',
      description: 'Curated selection of furniture, artwork, and decorative accessories.',
      features: ['Custom Furniture', 'Art Curation', 'Textile Selection', 'Accessory Styling'],
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    },
    {
      icon: FiTool,
      title: 'Project Management',
      description: 'Full project coordination from concept to completion.',
      features: ['Timeline Management', 'Contractor Coordination', 'Quality Control', 'Budget Management'],
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Consultation',
      description: 'We start with an in-depth consultation to understand your needs, style, and budget.'
    },
    {
      step: '02',
      title: 'Design Concept',
      description: 'Our team creates initial design concepts and mood boards for your approval.'
    },
    {
      step: '03',
      title: 'Development',
      description: 'We develop detailed plans, 3D renderings, and material specifications.'
    },
    {
      step: '04',
      title: 'Implementation',
      description: 'Our project managers coordinate all aspects of the design implementation.'
    },
    {
      step: '05',
      title: 'Final Styling',
      description: 'We add the finishing touches and style your space to perfection.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Interior Design Services - Elite Interior Design | Residential & Commercial</title>
        <meta name="description" content="Professional interior design services including residential design, commercial spaces, space planning, color consultation, and project management. Transform your space today." />
        <meta name="keywords" content="interior design services, residential design, commercial design, space planning, color consultation, furniture selection" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl font-bold text-white mb-6">Our Services</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                From concept to completion, we offer comprehensive interior design services 
                tailored to your unique needs and style preferences.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-700 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <SafeIcon icon={service.icon} className="h-10 w-10 text-yellow-400 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                    <p className="text-gray-300 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="text-gray-400 text-sm">
                          • {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-white mb-4">Our Design Process</h2>
              <p className="text-gray-300 max-w-2xl mx-auto">
                We follow a proven process to ensure every project is completed on time, 
                within budget, and exceeds your expectations.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {process.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="bg-yellow-500 text-black font-bold text-2xl w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-300 text-sm">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-white mb-6">Why Choose Elite Interior Design?</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-500 rounded-full p-1 mt-1">
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Expert Team</h3>
                      <p className="text-gray-300">Our experienced designers bring creativity and expertise to every project.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-500 rounded-full p-1 mt-1">
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Personalized Service</h3>
                      <p className="text-gray-300">We tailor our services to your specific needs and style preferences.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-500 rounded-full p-1 mt-1">
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Quality Guarantee</h3>
                      <p className="text-gray-300">We stand behind our work with a comprehensive quality guarantee.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-500 rounded-full p-1 mt-1">
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Timely Delivery</h3>
                      <p className="text-gray-300">Projects are completed on schedule without compromising quality.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1615529328331-f8917597711f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                  alt="Design Process"
                  className="rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold text-black mb-4">Ready to Get Started?</h2>
              <p className="text-black mb-8 text-lg">
                Contact us today for a free consultation and let's discuss your project.
              </p>
              <a
                href="#contact"
                className="inline-block bg-black hover:bg-gray-800 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-300"
              >
                Schedule Consultation
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;